import React, { useMemo } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { ExternalLink, ChevronLeft, ChevronRight, Users, ChevronDown, ChevronUp, Mail, Phone, Search, Eye, Download, Copy, Check, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw, Clock, UserPlus } from "lucide-react";
import { useState, Fragment, useEffect } from "react";
import { Company, getCompanies, CompanyResponse } from "@/lib/api";
import { useAppStore } from "@/lib/store";
import { useUrlProject } from "@/hooks/useUrlProject";
import { getApiUrl } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { AddMemberToACCDialog } from "@/components/AddMemberToACCDialog";
import { COMPANY_DATA_CONFIG, getUserStatField } from "@/config/company-data";

// Helper function to merge packages from all users
const mergeUserPackages = (userStats: any[]): string => {
  if (!userStats || !Array.isArray(userStats)) return '';
  
  const allPackages = new Set<string>();
  
  userStats.forEach(user => {
    if (user?.packages) {
      const packages = user.packages.split(',').map((p: string) => p.trim()).filter(Boolean);
      packages.forEach(pkg => allPackages.add(pkg));
    }
  });
  
  return Array.from(allPackages).sort().join(', ');
};

interface ProjectUser {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  autodeskId: string;
  companyId: string;
  companyName: string;
  jobTitle?: string;
  phone?: {
    number: string;
    phoneType: string;
    extension?: string;
  };
  roles: Array<{
    id: string;
    name: string;
  }>;
  status: 'active' | 'pending' | 'deleted';
  accessLevels: {
    accountAdmin: boolean;
    projectAdmin: boolean;
    executive: boolean;
  };
  products: Array<{
    key: string;
    access: string;
  }>;
  addedOn: string;
  imageUrl?: string;
}

interface RealCompany {
  id: string;
  name: string;
  type: string;
  initials: string;
  companyAccessCode: string;
  totalPackages: number;
  totalViews: number;
  totalDownloads: number;
  totalReturneds: number;
  totalAwards: number;
  lastActivity?: string;
  packages: string;
  users: ProjectUser[];
  // Percentage fields for display
  viewPercentage?: number;
  downloadPercentage?: number;
  returnPercentage?: number;
  awardPercentage?: number;
}

export default function ViewByCompany() {
  const [, setLocation] = useLocation();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set());
  const [tendererFilter, setTendererFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [packageCountFilter, setPackageCountFilter] = useState<'all' | 'hasPackages' | 'noPackages'>('all');
  const [copiedAccessCodes, setCopiedAccessCodes] = useState<Set<string>>(new Set());
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [selectedCompanyForPackages, setSelectedCompanyForPackages] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const { selectedProject } = useAppStore();
  const { urlProjectId } = useUrlProject();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Add ACC Member Dialog State
  const [isAddMemberDialogOpen, setIsAddMemberDialogOpen] = useState(false);
  

  // Handle member added callback
  const handleMemberAdded = () => {
    // Refresh the data - 清除所有相关缓存
    queryClient.invalidateQueries({ queryKey: ["project-users"] });
    queryClient.invalidateQueries({ queryKey: ["user-access-stats"] });
    queryClient.invalidateQueries({ queryKey: ["company-stats"] });
    queryClient.invalidateQueries({ queryKey: ["activities-data"] });
    queryClient.invalidateQueries({ queryKey: ["acc-companies"] });
    queryClient.invalidateQueries({ queryKey: ["companies-data", selectedProject?.id] }); // 修复：使用正确的 queryKey
    
    // 强制重新获取数据
    queryClient.refetchQueries({ queryKey: ["companies-data", selectedProject?.id] }); // 修复：强制刷新公司数据
    queryClient.refetchQueries({ queryKey: ["user-access-stats"] });
    queryClient.refetchQueries({ queryKey: ["company-stats"] });
  };

  // Get project users data (single request for both companies and users)
  const { data: projectUsers, isLoading: isLoadingUsers, isFetching: isFetchingUsers } = useQuery<ProjectUser[]>({
    queryKey: ["project-users", selectedProject?.id],
    queryFn: async () => {
      if (!selectedProject?.id) return [];
      const response = await fetch(getApiUrl(`/api/autodesk/projects/${selectedProject.id}/users`));
      if (!response.ok) {
        // Don't retry on 401 errors - this is expected for some projects
        if (response.status === 401) {
          console.warn('Project users API returned 401 - user not authenticated for this project');
          return [];
        }
        throw new Error('Failed to fetch project users');
      }
      return response.json();
    },
    enabled: !!selectedProject?.id,
    refetchInterval: (data, query) => {
      // Stop refetching if we get 401 errors
      if (query?.state?.error?.message?.includes('401')) {
        return false;
      }
      return 30000; // Increase to 30 seconds to reduce load
    },
    refetchOnWindowFocus: false, // Disable refetch on window focus to reduce load
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
    staleTime: 60000, // Consider data stale after 1 minute
    retry: (failureCount, error) => {
      // Don't retry on 401 errors
      if (error?.message?.includes('401')) {
        return false;
      }
      return failureCount < 1; // Reduce retries
    },
    refetchOnMount: true, // Always refetch when component mounts
  });

  // ⚠️ CRITICAL: Get companies data directly from activities-data endpoint
  // DO NOT MODIFY: This is the correct way to fetch company data with statistics
  // Previous attempts to derive from projectUsers caused data loss
  const { data: companiesData, isLoading: isLoadingCompanies, isFetching: isFetchingCompanies } = useQuery<RealCompany[]>({
    queryKey: ["companies-data", selectedProject?.id],
    queryFn: async () => {
      if (!selectedProject?.id) return [];
      const response = await fetch(getApiUrl(`${COMPANY_DATA_CONFIG.PRIMARY_DATA_SOURCE}&projectId=${selectedProject.id}`));
      if (!response.ok) {
        // Don't retry on 401 errors
        if (response.status === 401) {
          console.warn('Companies data API returned 401 - user not authenticated');
          return [];
        }
        throw new Error('Failed to fetch companies data');
      }
      return response.json();
    },
    enabled: !!selectedProject?.id,
    refetchInterval: (data, query) => {
      // Stop refetching if we get 401 errors
      if (query?.state?.error?.message?.includes('401')) {
        return false;
      }
      return 30000; // Increase to 30 seconds to reduce load
    },
    refetchOnWindowFocus: false, // Disable refetch on window focus to reduce load
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
    staleTime: 60000, // Consider data stale after 1 minute
    retry: (failureCount, error) => {
      // Don't retry on 401 errors
      if (error?.message?.includes('401')) {
        return false;
      }
      return failureCount < 1; // Reduce retries
    },
    refetchOnMount: true, // Always refetch when component mounts
  });

  // Get user-level access statistics for each company
  const { data: userStatsData, isLoading: isLoadingUserStats, isFetching: isFetchingUserStats } = useQuery<{[companyName: string]: any[]}>({
    queryKey: ["user-access-stats", companiesData?.map(c => c.name).filter(Boolean)],
    queryFn: async () => {
      if (!companiesData?.length) return {};
      
      const stats: {[companyName: string]: any[]} = {};
      
      for (const company of companiesData) {
        if (company.name) {
          try {
            const response = await fetch(getApiUrl(`/api/user-access-stats?companyName=${encodeURIComponent(company.name)}`));
            if (response.ok) {
              const data = await response.json();
              // API directly returns user stats array
              stats[company.name] = Array.isArray(data) ? data : [];
            } else {
              stats[company.name] = [];
            }
          } catch (error) {
            console.error(`Failed to fetch stats for ${company.name}:`, error);
            stats[company.name] = [];
          }
        }
      }
      
      return stats;
    },
    enabled: !!companiesData?.length,
    refetchInterval: 15000, // Refresh every 15 seconds
    refetchOnWindowFocus: true, // Refresh when window gains focus
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
    staleTime: 10000, // Consider data stale after 10 seconds
    retry: 2, // Retry twice on failure
    refetchOnMount: true, // Always refetch when component mounts
  });

  // Get company-level statistics for each company
  const { data: companyStatsData, isLoading: isLoadingCompanyStats, isFetching: isFetchingCompanyStats } = useQuery<{[companyName: string]: any}>({
    queryKey: ["company-stats", companiesData?.map(c => c.name).filter(Boolean)],
    queryFn: async () => {
      if (!companiesData?.length) return {};
      
      const stats: {[companyName: string]: any} = {};
      
      for (const company of companiesData) {
        if (company.name) {
          try {
            const response = await fetch(getApiUrl(`/api/company-stats?companyName=${encodeURIComponent(company.name)}`));
            if (response.ok) {
              const data = await response.json();
              stats[company.name] = data;
            } else {
              stats[company.name] = null;
            }
          } catch (error) {
            console.error(`Failed to fetch company stats for ${company.name}:`, error);
            stats[company.name] = null;
          }
        }
      }
      
      return stats;
    },
    enabled: !!companiesData?.length,
    refetchInterval: 15000, // Refresh every 15 seconds
    refetchOnWindowFocus: true, // Refresh when window gains focus
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
    staleTime: 10000, // Consider data stale after 10 seconds
    retry: 2, // Retry twice on failure
    refetchOnMount: true, // Always refetch when component mounts
  });





  // Transform companies data to match expected format
  const companiesByUser = companiesData?.reduce((acc, company) => {
    const companyId = company.id || company.name || 'unknown';
    
    // Get users for this company from project users data (ACC platform data)
    // This should show ALL companies that have members in ACC, regardless of tender status
    let companyUsers = projectUsers?.filter(user => user.companyName === company.name)?.map(user => ({
      id: user.id || 'unknown',
      name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      autodeskId: user.id || 'unknown',
      companyId: company.id || 'unknown',
      companyName: company.name || 'Unknown Company',
      jobTitle: user.jobTitle || '',
      phone: { number: '', phoneType: 'mobile' },
      roles: [],
      status: 'active' as const,
      accessLevels: {
        accountAdmin: false,
        projectAdmin: false,
        executive: false
      },
      products: [],
      addedOn: new Date().toISOString(),
      imageUrl: undefined
    })) || [];

    // Note: We don't use userStatsData as fallback for member count anymore
    // userStatsData is only used for activity statistics (views, downloads, etc.)
    // Member count should always come from ACC platform data (projectUsers)
    
    // Add data validation to prevent display of corrupted data
    if (companyUsers.length > 1000) {
      console.warn(`[DATA VALIDATION] Company ${company.name} has suspiciously high member count: ${companyUsers.length}`);
      companyUsers = []; // Reset to empty if data seems corrupted
    }
    
    // Fallback: If no users from projectUsers (due to 401 error), try to get from userStatsData
    if (companyUsers.length === 0 && userStatsData?.[company.name]) {
      const userStats = userStatsData[company.name];
      console.log(`[FALLBACK] Using userStatsData for company ${company.name}: ${userStats.length} users`);
      companyUsers = userStats.map((user: any, index: number) => ({
        id: user.id || user.autodesk_id || `user-${index}`,
        name: user.name || user.email || 'Unknown User',
        firstName: user.first_name || user.name?.split(' ')[0] || '',
        lastName: user.last_name || user.name?.split(' ').slice(1).join(' ') || '',
        email: user.email || '',
        autodeskId: user.autodesk_id || user.id || `user-${index}`,
        companyId: company.id || 'unknown',
        companyName: company.name || 'Unknown Company',
        jobTitle: user.job_title || '',
        phone: { number: user.phone || '', phoneType: 'mobile' },
        roles: [],
        status: 'active' as const,
        accessLevels: {
          accountAdmin: false,
          projectAdmin: false,
          executive: false
        },
        products: [],
        addedOn: new Date().toISOString(),
        imageUrl: undefined
      }));
    }
    
    acc[companyId] = {
      id: companyId,
      name: company.name || 'Unknown Company',
      type: 'Company',
      initials: company.initials || (company.name || 'Company').split(' ').map((word: string) => word.charAt(0)).join('').substring(0, 3),
      companyAccessCode: company.companyAccessCode || 'N/A',
      totalPackages: parseInt(String(company.totalPackages)) || 0,
      totalViews: parseInt(String(company.totalViews)) || 0,
      totalDownloads: parseInt(String(company.totalDownloads)) || 0,
      totalReturneds: parseInt(String(company.totalReturneds)) || 0,
      totalAwards: parseInt(String(company.totalAwards)) || 0,
      lastActivity: undefined,
      packages: company.packages || 'No packages',
      users: companyUsers || []
    } as RealCompany;
    
    return acc;
  }, {} as Record<string, RealCompany>) || {};

  // Convert to array for rendering and enhance with company stats
  const realCompanies = Object.values(companiesByUser).map((company: RealCompany) => {
    // Get company-level stats
    const companyStats = companyStatsData?.[company.name];
    const userStats = userStatsData?.[company.name] || [];
    
    if (companyStats) {
      // Add data validation to prevent display of corrupted statistics
      const validateStat = (value: any, maxValue: number = 1000) => {
        const num = parseInt(String(value)) || 0;
        return num > maxValue ? 0 : num;
      };
      
      // Use company-level stats for the main row display
      return {
        ...company,
        companyAccessCode: userStats[0]?.company_access_code || company.companyAccessCode || 'N/A',
        totalPackages: validateStat(companyStats.totalPackages || company.totalPackages, 100),
        totalViews: validateStat(companyStats.viewedPackages || company.totalViews, 1000),
        totalDownloads: validateStat(companyStats.downloadedPackages || company.totalDownloads, 1000),
        totalReturneds: validateStat(companyStats.returnedPackages || company.totalReturneds, 1000),
        totalAwards: validateStat(companyStats.awardedPackages || company.totalAwards, 100),
        packages: mergeUserPackages(userStats),
        // Add percentage information for display
        viewPercentage: companyStats.viewPercentage || 0,
        downloadPercentage: companyStats.downloadPercentage || 0,
        returnPercentage: companyStats.returnPercentage || 0,
        awardPercentage: companyStats.awardPercentage || 0,
      } as RealCompany;
    } else if (userStats && Array.isArray(userStats) && userStats.length > 0) {
      // Add data validation for fallback stats
      const validateStat = (value: any, maxValue: number = 1000) => {
        const num = parseInt(String(value)) || 0;
        return num > maxValue ? 0 : num;
      };
      
      // Fallback to user stats aggregation if company stats not available
      const aggregatedStats = userStats.reduce((acc: any, user: any) => {
        return {
          companyAccessCode: user?.company_access_code || acc.companyAccessCode || company.companyAccessCode || 'N/A',
          totalPackages: validateStat(Math.max(acc.totalPackages || 0, user?.total_packages || 0, company.totalPackages || 0), 100),
          totalViews: validateStat((acc.totalViews || 0) + (user?.viewed_packages || 0), 1000),
          totalDownloads: validateStat((acc.totalDownloads || 0) + (user?.downloaded_packages || 0), 1000),
          totalReturneds: validateStat((acc.totalReturneds || 0) + (user?.returned_packages || 0), 1000),
          totalAwards: validateStat(company.totalAwards || 0, 100),
          packages: mergeUserPackages(userStats),
        };
      }, {});
      
      return {
        ...company,
        ...aggregatedStats,
      } as RealCompany;
    }
    return company as RealCompany;
  });
  
  // Apply search, filters, and sorting
  const filteredAndSortedCompanies = realCompanies
    .filter((company: RealCompany) => {
      const companyName = company.name || '';
      const companyPackages = company.packages || '';
      const companyAccessCode = company.companyAccessCode || '';
      
      const matchesSearch = !searchTerm || 
        companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        companyPackages.toLowerCase().includes(searchTerm.toLowerCase()) ||
        companyAccessCode.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Tenderer filter
      const isTenderer = companyPackages && companyPackages.trim() !== '' && companyPackages !== 'No packages';
      const matchesTendererFilter = tendererFilter === 'all' || 
        (tendererFilter === 'yes' && isTenderer) ||
        (tendererFilter === 'no' && !isTenderer);
      
      // Package count filter
      const hasPackages = (company.totalPackages || 0) > 0;
      const matchesPackageFilter = packageCountFilter === 'all' ||
        (packageCountFilter === 'hasPackages' && hasPackages) ||
        (packageCountFilter === 'noPackages' && !hasPackages);
      
      return matchesSearch && matchesTendererFilter && matchesPackageFilter;
    })
    .sort((a: RealCompany, b: RealCompany) => {
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'name':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'accessCode':
          aValue = a.companyAccessCode || '';
          bValue = b.companyAccessCode || '';
          break;
        case 'packages':
          aValue = a.totalPackages || 0;
          bValue = b.totalPackages || 0;
          break;
        case 'packageNames':
          aValue = a.packages?.toLowerCase() || '';
          bValue = b.packages?.toLowerCase() || '';
          break;
        case 'isTenderer':
          const aIsTenderer = a.packages && a.packages.trim() !== '' && a.packages !== 'No packages';
          const bIsTenderer = b.packages && b.packages.trim() !== '' && b.packages !== 'No packages';
          aValue = aIsTenderer ? 1 : 0;
          bValue = bIsTenderer ? 1 : 0;
          break;
        case 'views':
          aValue = a.totalViews || 0;
          bValue = b.totalViews || 0;
          break;
        case 'downloads':
          aValue = a.totalDownloads || 0;
          bValue = b.totalDownloads || 0;
          break;
        case 'returneds':
          aValue = a.totalReturneds || 0;
          bValue = b.totalReturneds || 0;
          break;
        case 'awards':
          aValue = a.totalAwards || 0;
          bValue = b.totalAwards || 0;
          break;
        default:
          aValue = 0;
          bValue = 0;
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      } else {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });

  // ⚠️ CRITICAL: Combined loading state - DO NOT MODIFY
  // This prevents duplicate variable declarations and ensures proper loading state
  const isLoading = isLoadingCompanies || isLoadingUsers || isLoadingUserStats || isLoadingCompanyStats;

  const toggleCompanyExpansion = (companyId: string) => {
    const newExpanded = new Set(expandedCompanies);
    if (newExpanded.has(companyId)) {
      newExpanded.delete(companyId);
    } else {
      newExpanded.add(companyId);
    }
    setExpandedCompanies(newExpanded);
  };

  const statusColors = {
    "On Track": "bg-green-100 text-green-800",
    "At Risk": "bg-yellow-100 text-yellow-800",
    Delayed: "bg-red-100 text-red-800",
  };

  const progressColors = {
    "On Track": "bg-green-500",
    "At Risk": "bg-yellow-500",
    Delayed: "bg-red-500",
  };

  const handlePrevPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNextPage = () => {
    // Pagination logic for real companies if needed
    setPage(page + 1);
  };

  const handleCopyAccessCode = async (accessCode: string) => {
    try {
      await navigator.clipboard.writeText(accessCode);
      setCopiedAccessCodes(prev => new Set(prev).add(accessCode));
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedAccessCodes(prev => {
          const newSet = new Set(prev);
          newSet.delete(accessCode);
          return newSet;
        });
      }, 2000);
    } catch (error) {
      console.error('Failed to copy access code:', error);
    }
  };

  const handlePackageClick = async (packageNumber: string) => {
    // Get the current project ID from either selected project or URL
    // Ensure we use the full project ID format (with b. prefix) for consistency
    let currentProjectId = urlProjectId || selectedProject?.id || 'b.c53cca91-64e3-4f2d-b329-75d89031bc99';
    
    // If the project ID doesn't have the b. prefix, add it
    if (currentProjectId && !currentProjectId.startsWith('b.')) {
      currentProjectId = `b.${currentProjectId}`;
    }
    
    const trimmedPackageNumber = packageNumber.trim();
    
    // Check if this is an addendum package (contains -AD)
    const isAddendum = trimmedPackageNumber.includes('-AD');
    
    try {
      // Fetch all packages for the project
      const response = await fetch(getApiUrl(`/api/tender-packages?projectId=${currentProjectId}`));
      if (response.ok) {
        const packagesData = await response.json();
        
        if (isAddendum) {
          // For addendum packages, find both the addendum and its parent
          const addendumPackage = packagesData.find((pkg: any) => 
            pkg.packageNumber === trimmedPackageNumber && pkg.isAddendum
          );
          
          if (addendumPackage) {
            // Extract addendum number from package number (e.g., "WP0001-AD01" -> "ad01")
            const addendumMatch = trimmedPackageNumber.match(/-AD(\d+)$/);
            if (addendumMatch) {
              const addendumNumber = addendumMatch[1];
              const addendumId = `ad${addendumNumber.padStart(2, '0')}`;
              
              // If addendum has parentPackageId, use it directly
              if (addendumPackage.parentPackageId) {
                setLocation(`/tender-packages/projects/${currentProjectId}/${addendumPackage.parentPackageId}/${addendumId}`);
                return;
              }
              
              // Fallback: try to find parent by package number prefix
              const packagePrefix = trimmedPackageNumber.split('-AD')[0];
              const parentPackage = packagesData.find((pkg: any) => 
                pkg.packageNumber === packagePrefix && !pkg.isAddendum
              );
              
              if (parentPackage) {
                setLocation(`/tender-packages/projects/${currentProjectId}/${parentPackage.id}/${addendumId}`);
                return;
              }
            }
          } else {
            // If addendum package not found in list, try to find parent by prefix
            const packagePrefix = trimmedPackageNumber.split('-AD')[0];
            const parentPackage = packagesData.find((pkg: any) => 
              pkg.packageNumber === packagePrefix && !pkg.isAddendum
            );
            
            if (parentPackage) {
              const addendumMatch = trimmedPackageNumber.match(/-AD(\d+)$/);
              if (addendumMatch) {
                const addendumNumber = addendumMatch[1];
                const addendumId = `ad${addendumNumber.padStart(2, '0')}`;
                setLocation(`/tender-packages/projects/${currentProjectId}/${parentPackage.id}/${addendumId}`);
                return;
              }
            }
          }
        } else {
          // For main packages, find the package directly
          const targetPackage = packagesData.find((pkg: any) => 
            pkg.packageNumber === trimmedPackageNumber && !pkg.isAddendum
          );
          
          if (targetPackage) {
            // Navigate directly to the package detail page
            setLocation(`/tender-packages/projects/${currentProjectId}/${targetPackage.id}`);
            return;
          }
        }
      }
    } catch (error) {
      console.error('Error finding package:', error);
    }
    
    // Fallback: navigate to search page if package not found
    setLocation(`/tender-packages/projects/${currentProjectId}?search=${encodeURIComponent(trimmedPackageNumber)}`);
  };

  const renderPackageLinks = (packages: string, companyId: string, companyName: string) => {
    if (!packages || packages === 'No packages') {
      return <span className="text-xs text-gray-600 text-center">No packages</span>;
    }
    
    const packageNumbers = packages.split(', ').filter(pkg => pkg.trim());
    const displayPackages = packageNumbers.slice(0, 4);
    const hasMore = packageNumbers.length > 4;
    
    const handleShowAll = () => {
      setSelectedCompanyForPackages(companyId);
      setShowPackageDialog(true);
    };
    
    return (
      <div className="flex flex-wrap gap-1 justify-center items-center">
        {displayPackages.map((packageNumber, index) => (
          <button
            key={index}
            onClick={() => handlePackageClick(packageNumber)}
            className="text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
          >
            {packageNumber.trim()}
            {index < displayPackages.length - 1 && <span className="text-gray-400">,</span>}
          </button>
        ))}
        {hasMore && <span className="text-xs text-gray-500">...</span>}
        {hasMore && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleShowAll();
            }}
            className="text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer ml-1"
          >
            - Show All
          </button>
        )}
      </div>
    );
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 text-gray-400" />;
    }
    return sortDirection === 'asc' ? 
      <ArrowUp className="h-3 w-3 text-blue-600" /> : 
      <ArrowDown className="h-3 w-3 text-blue-600" />;
  };

  if (isLoading) {
    return (
      <Card className="mb-6">
        <CardHeader className="border-b border-gray-200 p-6">
          <div className="h-8 bg-gray-200 animate-pulse rounded w-1/4" />
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            {[...Array(5)].map((_, index) => (
              <div key={index} className="flex items-center space-x-4">
                <div className="h-10 w-10 bg-gray-200 animate-pulse rounded-full" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 animate-pulse rounded w-1/4" />
                  <div className="h-3 bg-gray-200 animate-pulse rounded w-1/6" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="mb-6">
      <CardContent className="p-0">
        {/* Search Bar and Filters in One Row */}
        <div className="p-6 border-b border-gray-200">
          {/* Refresh Status Indicator and Add User Button */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Badge variant="secondary" className={`flex items-center ${isFetchingUsers || isFetchingUserStats || isFetchingCompanyStats ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-blue-100 text-blue-700 border-blue-200'}`}>
                <Clock className="w-3 h-3 mr-1" />
                {isFetchingUsers || isFetchingUserStats || isFetchingCompanyStats ? 'Updating...' : 'Company Data'}
              </Badge>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => setIsAddMemberDialogOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white shadow-md"
                size="sm"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add ACC Users
              </Button>
              <div className="text-sm text-gray-500">
                Auto-refreshes every 15 seconds
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-4">
            {/* Search Bar */}
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search companies or packages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Is Tenderer Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Is Tenderer:</label>
              <Select value={tendererFilter} onValueChange={(value) => setTendererFilter(value as 'all' | 'yes' | 'no')}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Package Count Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Package Count:</label>
              <Select value={packageCountFilter} onValueChange={(value) => setPackageCountFilter(value as 'all' | 'hasPackages' | 'noPackages')}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="hasPackages">Has Packages</SelectItem>
                  <SelectItem value="noPackages">No Packages</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            
            {/* Clear Filters Button */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                setSearchTerm('');
                setTendererFilter('all');
                setPackageCountFilter('all');
                setSortField('name');
                setSortDirection('asc');
              }}
              className="whitespace-nowrap"
            >
              Clear Filters
            </Button>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[160px]">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-1 hover:text-gray-700 transition-colors"
                >
                  Company
                  {getSortIcon('name')}
                </button>
              </th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                <button
                  onClick={() => handleSort('accessCode')}
                  className="flex items-center justify-center gap-1 hover:text-gray-700 transition-colors mx-auto"
                >
                  Access Code
                  {getSortIcon('accessCode')}
                </button>
              </th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[40px]">
                <button
                  onClick={() => handleSort('packages')}
                  className="flex items-center justify-center gap-1 hover:text-gray-700 transition-colors mx-auto"
                >
                  Packages
                  {getSortIcon('packages')}
                </button>
              </th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px]">
                <button
                  onClick={() => handleSort('packageNames')}
                  className="flex items-center justify-center gap-1 hover:text-gray-700 transition-colors mx-auto"
                >
                  Package No.
                  {getSortIcon('packageNames')}
                </button>
              </th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[60px]">
                <button
                  onClick={() => handleSort('isTenderer')}
                  className="flex items-center justify-center gap-1 hover:text-gray-700 transition-colors mx-auto"
                >
                  Is Tenderer
                  {getSortIcon('isTenderer')}
                </button>
              </th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[70px]">
                <button
                  onClick={() => handleSort('views')}
                  className="flex items-center justify-center gap-1 hover:text-gray-700 transition-colors mx-auto"
                >
                  <Eye className="w-4 h-4" />
                  Views
                  {getSortIcon('views')}
                </button>
              </th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px]">
                <button
                  onClick={() => handleSort('downloads')}
                  className="flex items-center justify-center gap-1 hover:text-gray-700 transition-colors mx-auto"
                >
                  <Download className="w-4 h-4" />
                  Downloads
                  {getSortIcon('downloads')}
                </button>
              </th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[70px]">
                <button
                  onClick={() => handleSort('returneds')}
                  className="flex items-center justify-center gap-1 hover:text-gray-700 transition-colors mx-auto"
                >
                  Returneds
                  {getSortIcon('returneds')}
                </button>
              </th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[70px]">
                <button
                  onClick={() => handleSort('awards')}
                  className="flex items-center justify-center gap-1 hover:text-gray-700 transition-colors mx-auto"
                >
                  Awards
                  {getSortIcon('awards')}
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredAndSortedCompanies?.map((company: any) => {
              const companyUsers = company.users || [];
              const isExpanded = expandedCompanies.has(String(company.id));
              return (
                <Fragment key={company.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleCompanyExpansion(String(company.id))}
                          className="mr-2 p-1 h-6 w-6"
                        >
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {company.name}
                          </div>
                          {companyUsers.length > 0 && (
                            <div className="mt-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                <Users className="w-3 h-3 mr-1" />
                                {companyUsers.length} member{companyUsers.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Badge variant="outline" className="font-mono bg-gray-50">
                          {company.companyAccessCode || 'N/A'}
                        </Badge>
                        {company.companyAccessCode && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopyAccessCode(company.companyAccessCode)}
                            className="h-6 w-6 p-0 hover:bg-blue-50"
                          >
                            {copiedAccessCodes.has(company.companyAccessCode) ? (
                              <Check className="h-3 w-3 text-green-600" />
                            ) : (
                              <Copy className="h-3 w-3 text-gray-500" />
                            )}
                          </Button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-center">
                      <Badge variant="outline" className="bg-blue-50 text-blue-700">
                        {company.totalPackages || 0}
                      </Badge>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-center">
                      <div className="w-full flex justify-center">
                        {renderPackageLinks(company.packages, String(company.id), company.name)}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-center">
                      <Badge variant="outline" className={`${company.packages && company.packages.trim() !== '' && company.packages !== 'No packages' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                        {company.packages && company.packages.trim() !== '' && company.packages !== 'No packages' ? 'Yes' : 'No'}
                      </Badge>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-center">
                      <Badge variant="outline" className="bg-orange-50 text-orange-700">
                        {company.totalViews || 0}/{company.totalPackages || 0}
                      </Badge>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-center">
                      <Badge variant="outline" className="bg-green-50 text-green-700">
                        {company.totalDownloads || 0}/{company.totalPackages || 0}
                      </Badge>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-center">
                      <Badge variant="outline" className="bg-purple-50 text-purple-700">
                        {company.totalReturneds || 0}/{company.totalPackages || 0}
                      </Badge>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-center">
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                        {company.totalAwards || 0}/{company.totalPackages || 0}
                      </Badge>
                    </td>

                  </tr>
                  
                  {/* Expandable Member Section */}
                  {isExpanded && companyUsers.length > 0 && (
                    <tr>
                      <td colSpan={9} className="px-6 py-4 bg-gray-50">
                        <div className="space-y-4">
                          <h4 className="text-sm font-medium text-gray-900 flex items-center">
                            <Users className="w-4 h-4 mr-2" />
                            Team Members ({companyUsers.length})
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {companyUsers.map((user: ProjectUser) => (
                              <div key={user.id} className="bg-white p-4 rounded-lg border border-gray-200">
                                <div className="flex items-start space-x-3">
                                  <div className="flex-shrink-0">
                                    <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                                      <span className="text-blue-600 text-sm font-medium">
                                        {user.firstName?.charAt(0) || user.name?.charAt(0) || '?'}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {user.name || `${user.firstName} ${user.lastName}`}
                                    </p>
                                    {user.jobTitle && (
                                      <p className="text-xs text-gray-500 truncate">{user.jobTitle}</p>
                                    )}
                                    <div className="mt-2 space-y-1">
                                      {user.email && (
                                        <div className="flex items-center text-xs text-gray-500">
                                          <Mail className="w-3 h-3 mr-1" />
                                          <span className="truncate">{user.email}</span>
                                        </div>
                                      )}
                                      {user.phone?.number && (
                                        <div className="flex items-center text-xs text-gray-500">
                                          <Phone className="w-3 h-3 mr-1" />
                                          <span>{user.phone.number}</span>
                                        </div>
                                      )}
                                    </div>
                                    {user.roles.length > 0 && (
                                      <div className="mt-2 flex flex-wrap gap-1">
                                        {user.roles.slice(0, 2).map((role: { id: string; name: string }) => (
                                          <span key={role.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                            {role.name}
                                          </span>
                                        ))}
                                        {user.roles.length > 2 && (
                                          <span className="text-xs text-gray-500">+{user.roles.length - 2} more</span>
                                        )}
                                      </div>
                                    )}
                                    
                                    {/* Member Activity Stats */}
                                    <div className="mt-2 pt-2 border-t border-gray-100">
                                      
                                      <div className="grid grid-cols-2 gap-1">
                                        <div className="flex items-center justify-between">
                                          <span className="text-xs text-gray-500">Packages:</span>
                                          <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs">
                                            {(() => {
                                              const userStats = (userStatsData as any)?.[company.name] || [];
                                              const userStat = userStats.find((stat: any) => stat.email === user.email);
                                              return getUserStatField(userStat, 'TOTAL_PACKAGES');
                                            })()}
                                          </Badge>
                                        </div>
                                        <div className="flex items-center justify-between">
                                          <span className="text-xs text-gray-500">Views:</span>
                                          <Badge variant="outline" className="bg-orange-50 text-orange-700 text-xs">
                                            {(() => {
                                              const userStats = (userStatsData as any)?.[company.name] || [];
                                              const userStat = userStats.find((stat: any) => stat.email === user.email);
                                              return getUserStatField(userStat, 'VIEWED_PACKAGES');
                                            })()}
                                          </Badge>
                                        </div>
                                        <div className="flex items-center justify-between">
                                          <span className="text-xs text-gray-500">Downloads:</span>
                                          <Badge variant="outline" className="bg-green-50 text-green-700 text-xs">
                                            {(() => {
                                              const userStats = (userStatsData as any)?.[company.name] || [];
                                              const userStat = userStats.find((stat: any) => stat.email === user.email);
                                              return getUserStatField(userStat, 'DOWNLOADED_PACKAGES');
                                            })()}
                                          </Badge>
                                        </div>
                                        <div className="flex items-center justify-between">
                                          <span className="text-xs text-gray-500">Returneds:</span>
                                          <Badge variant="outline" className="bg-purple-50 text-purple-700 text-xs">
                                            {(() => {
                                              const userStats = (userStatsData as any)?.[company.name] || [];
                                              const userStat = userStats.find((stat: any) => stat.email === user.email);
                                              return getUserStatField(userStat, 'RETURNED_PACKAGES');
                                            })()}
                                          </Badge>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
                      </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between p-4">
          <div className="text-sm text-gray-500">
            Showing {filteredAndSortedCompanies?.length || 0} companies with {(filteredAndSortedCompanies?.reduce((sum: number, comp: RealCompany) => sum + (comp.users?.length || 0), 0) || 0) as number} total members
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevPage}
              disabled={page === 1}
              className="flex items-center gap-1"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={true}
              className="flex items-center gap-1"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>

      {/* Add ACC Member Dialog */}
      {(selectedProject?.id || urlProjectId) && (
        <AddMemberToACCDialog
          isOpen={isAddMemberDialogOpen}
          onClose={() => setIsAddMemberDialogOpen(false)}
          projectId={selectedProject?.id || urlProjectId || ''}
          onMemberAdded={handleMemberAdded}
        />
      )}

      {/* Show All Packages Dialog */}
      <Dialog open={showPackageDialog} onOpenChange={setShowPackageDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-900">All Packages</DialogTitle>
            <DialogDescription className="text-gray-600">
              View all packages for this company
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {selectedCompanyForPackages && (() => {
              const company = filteredAndSortedCompanies?.find(c => String(c.id) === selectedCompanyForPackages);
              if (!company || !company.packages || company.packages === 'No packages') {
                return (
                  <div className="text-center py-8 text-gray-500">
                    No packages available.
                  </div>
                );
              }
              
              const packageNumbers = company.packages.split(', ').filter(pkg => pkg.trim());
              
              return (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    {company.name} - All Packages ({packageNumbers.length})
                  </h4>
                  <div className="space-y-2">
                    {packageNumbers.map((packageNumber, index) => (
                      <div key={index} className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{packageNumber.trim()}</p>
                            <p className="text-sm text-gray-600">Package</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              handlePackageClick(packageNumber);
                              setShowPackageDialog(false);
                            }}
                          >
                            View
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>

          <DialogFooter className="border-t border-gray-200 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowPackageDialog(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
    </>
  );
}